import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@bcc/components/ui";
import { useTranslation } from "react-i18next";
import type { SetupGuidePage } from "../tour.types";
import { setTourTestId } from "../tour.utils";

export interface TourWelcomeCopyKeys {
  readonly titleKey: string;
  /** Receives `{{stepCount}}` */
  readonly descriptionKey: string;
  readonly startKey: string;
  readonly skipKey: string;
}

export const TOUR_WELCOME_COPY_KEYS: TourWelcomeCopyKeys = {
  titleKey: "tour.welcome.title",
  descriptionKey: "tour.welcome.description",
  startKey: "tour.welcome.start",
  skipKey: "tour.welcome.skip",
};

/** Welcome copy for the onboarding setup guide; the description differs per page */
export const buildSetupGuideWelcomeCopyKeys = (page: SetupGuidePage): TourWelcomeCopyKeys => ({
  titleKey: "tour.setupGuide.welcome.title",
  descriptionKey: `tour.setupGuide.welcome.description.${page}`,
  startKey: "tour.setupGuide.welcome.start",
  skipKey: "tour.setupGuide.welcome.skip",
});

/** Welcome copy for the "what's new" tour shown to users who already finished an older tour */
export const WHATS_NEW_WELCOME_COPY_KEYS: TourWelcomeCopyKeys = {
  titleKey: "tour.whatsNew.welcome.title",
  descriptionKey: "tour.whatsNew.welcome.description",
  startKey: "tour.whatsNew.welcome.start",
  skipKey: "tour.whatsNew.welcome.skip",
};

export interface TourWelcomeDialogProps {
  readonly isOpen: boolean;
  readonly stepCount: number;
  readonly onBegin: () => void;
  /** Closing the welcome dialog counts as skipping (marks the tour completed) */
  readonly onSkip: () => void;
  /** Defaults to the full tour's welcome copy */
  readonly copyKeys?: TourWelcomeCopyKeys;
  /** Names of the features being announced, listed under the description */
  readonly featureTitles?: readonly string[];
}

export function TourWelcomeDialog({
  isOpen,
  stepCount,
  onBegin,
  onSkip,
  copyKeys = TOUR_WELCOME_COPY_KEYS,
  featureTitles = [],
}: Readonly<TourWelcomeDialogProps>) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onSkip();
      }}
    >
      <DialogContent className="z-[70]" testId={setTourTestId("welcome-dialog")}>
        <DialogHeader>
          <DialogTitle testId={setTourTestId("welcome-title")}>{t(copyKeys.titleKey) as string}</DialogTitle>
          <DialogDescription testId={setTourTestId("welcome-description")}>
            {t(copyKeys.descriptionKey, { stepCount }) as string}
          </DialogDescription>
        </DialogHeader>
        {featureTitles.length > 0 && (
          <ul className="list-disc space-y-1 pl-5 text-sm" data-testid={setTourTestId("welcome-features")}>
            {featureTitles.map((title) => (
              <li key={title}>{title}</li>
            ))}
          </ul>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onSkip} testId={setTourTestId("welcome-skip")}>
            {t(copyKeys.skipKey) as string}
          </Button>
          <Button onClick={onBegin} testId={setTourTestId("welcome-start")}>
            {t(copyKeys.startKey) as string}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
